import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Trash2 } from 'lucide-react';
import api, { ernaehrungAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';

/* E17 Kurs-Ernährungs-Tipps (15.10.2026): Regel je Kursart — Push 60 Minuten nach dem Kurs-Check-in, nur für Kunden mit
   aktivem Ernährungscoaching, höchstens einer je Tag. Versand „smart“ (Entwurf in der Freigabe-Zentrale) oder „auto“. */

const fehlertext = (e, rest = 'Das hat nicht geklappt') => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : rest);
const feld = 'h-9 rounded-md border border-input bg-background px-2 text-sm';
const MODUS = { aus: 'Aus', smart: 'Smart — Entwurf in der Freigabe-Zentrale', auto: 'Auto — sofort als Push' };

export function KursTipps() {
  const [d, setD] = useState(null);
  const [rezepte, setRezepte] = useState([]);
  const [neu, setNeu] = useState({ kategorie: '', titel: '', text: '', rezept_id: '' });
  const [busy, setBusy] = useState(false);
  const laden = useCallback(() => ernaehrungAPI.kurstipps().then((r) => setD(r.data)).catch(() => {}), []);
  useEffect(() => { laden(); ernaehrungAPI.rezepte().then((r) => setRezepte(r.data?.rezepte || [])).catch(() => {}); }, [laden]);
  if (!d) return null;
  const tu = async (aufruf, ok) => {
    setBusy(true);
    try { await aufruf(); if (ok) toast.success(ok); await laden(); } catch (e) { toast.error(fehlertext(e)); } finally { setBusy(false); }
  };
  const kat = (k) => d.kategorien.find((x) => x.wert === k);
  return (
    <Card className="mt-4" data-testid="kurstipps">
      <CardHeader className="pb-2"><CardTitle className="text-base">Kurs-Ernährungs-Tipps</CardTitle></CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-muted-foreground">60 Minuten nach dem Kurs-Check-in bekommt ein Mitglied mit Ernährungscoaching einen Tipp zur Kursart — höchstens einen am Tag. „{'{kurs}'}“ im Text wird durch den Kursnamen ersetzt.</p>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium">Versand</span>
          <select value={d.einstellung.modus} disabled={busy} onChange={(e) => tu(() => ernaehrungAPI.kurstippsModus(e.target.value), 'Gespeichert')} className={feld} aria-label="Versand der Kurs-Tipps">
            {Object.entries(MODUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <ul className="divide-y rounded-md border">
          {d.regeln.length === 0 && <li className="p-2 text-xs text-muted-foreground">Noch keine Regel.</li>}
          {d.regeln.map((r) => (
            <li key={r.id} className="flex items-start gap-2 p-2">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5"><Badge variant="outline">{r.kategorie}</Badge><span className="font-medium">{r.titel}</span>{!r.aktiv && <Badge variant="outline">aus</Badge>}</div>
                <p className="text-xs text-muted-foreground">{r.text}{r.rezept_id ? ` · Rezept: ${(rezepte.find((x) => x.id === r.rezept_id) || {}).name || r.rezept_id}` : ''}</p>
              </div>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => tu(() => ernaehrungAPI.kurstippAendern(r.id, { kategorie: r.kategorie, titel: r.titel, text: r.text, rezept_id: r.rezept_id, aktiv: !r.aktiv }), r.aktiv ? 'Regel aus' : 'Regel an')}>{r.aktiv ? 'Aus' : 'An'}</Button>
              <Button size="icon" variant="ghost" className="h-8 w-8" aria-label="Regel löschen" disabled={busy} onClick={() => tu(() => ernaehrungAPI.kurstippLoeschen(r.id), 'Gelöscht')}><Trash2 className="h-4 w-4" /></Button>
            </li>
          ))}
        </ul>
        <form className="grid grid-cols-1 gap-2 rounded-md border p-2 sm:grid-cols-[10rem_1fr]" onSubmit={(e) => { e.preventDefault(); tu(() => ernaehrungAPI.kurstippNeu({ ...neu, rezept_id: neu.rezept_id || null }).then(() => setNeu({ kategorie: '', titel: '', text: '', rezept_id: '' })), 'Regel angelegt'); }}>
          <select value={neu.kategorie} required onChange={(e) => setNeu({ ...neu, kategorie: e.target.value })} className={feld} aria-label="Kursart">
            <option value="">Kursart …</option>
            {d.kategorien.map((k) => <option key={k.wert} value={k.wert}>{k.wert} (z. B. {k.beispiel})</option>)}
          </select>
          <Input value={neu.titel} maxLength={60} placeholder="Titel (Standard: P1 Tipp nach deinem Kurs)" onChange={(e) => setNeu({ ...neu, titel: e.target.value })} className="h-9 text-sm" />
          <select value={neu.rezept_id} onChange={(e) => setNeu({ ...neu, rezept_id: e.target.value })} className={feld} aria-label="Rezept">
            <option value="">ohne Rezept</option>
            {rezepte.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
          <Input value={neu.text} required minLength={10} maxLength={300} placeholder="z. B. Nach dem Sparring: innerhalb von 2 Stunden Protein + Kohlenhydrate." onChange={(e) => setNeu({ ...neu, text: e.target.value })} className="h-9 text-sm" />
          <div className="sm:col-span-2"><Button type="submit" size="sm" disabled={busy || !neu.kategorie}>{busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Regel anlegen</Button>{neu.kategorie && kat(neu.kategorie) && <span className="ml-2 text-xs text-muted-foreground">gilt für alle Kurse der Art {neu.kategorie}</span>}</div>
        </form>
      </CardContent>
    </Card>
  );
}
